
import React from 'react';
import { FileText, User, Edit3 } from 'lucide-react';
import { DiffEntry } from '@/types/sections';

interface DiffViewerProps {
  diffs: DiffEntry[];
}

export const DiffViewer: React.FC<DiffViewerProps> = ({ diffs }) => {
  if (!diffs || diffs.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic p-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 justify-center">
          <FileText className="w-4 h-4" />
          <span>No hay cambios registrados</span>
        </div>
      </div>
    );
  }

  const formatDiffContent = (diffHtml: string) => {
    // Clean up and improve the diff HTML display
    let cleanHtml = diffHtml
      .replace(/New Correction:/g, '<div class="diff-addition"><strong>✓ Nuevo contenido:</strong>')
      .replace(/Deleted:/g, '<div class="diff-deletion"><strong>✗ Eliminado:</strong>')
      .replace(/Eliminado:/g, '<div class="diff-deletion"><strong>✗ Eliminado:</strong>')
      .replace(/\n/g, '<br/>');
    
    // Close any unclosed divs
    const openDivs = (cleanHtml.match(/<div/g) || []).length;
    const closeDivs = (cleanHtml.match(/<\/div>/g) || []).length;
    
    if (openDivs > closeDivs) {
      for (let i = 0; i < (openDivs - closeDivs); i++) {
        cleanHtml += '</div>';
      }
    }
    
    return cleanHtml;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <Edit3 className="w-4 h-4 text-blue-600" />
          Historial de cambios
          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
            {diffs.length}
          </span>
        </h4>
      </div>
      
      <div className="max-h-80 overflow-y-auto">
        <div className="space-y-0">
          {diffs.map((diff, index) => (
            <div 
              key={index} 
              className={`border-b border-gray-100 last:border-b-0 ${
                index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
              } transition-colors hover:bg-blue-50`}
            >
              {/* Header con información del editor */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-start mb-3">
                  <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
                    <User className="w-3 h-3 text-gray-600" />
                    <span className="text-xs font-medium text-gray-700">
                      {diff.editor}
                    </span>
                  </div>
                </div>
                
                {/* Contenido del diff mejorado */}
                <div className="diff-content bg-white rounded-md border border-gray-200 p-3">
                  <div 
                    className="text-sm leading-relaxed
                      [&_.diff-addition]:bg-green-50 [&_.diff-addition]:border-l-4 [&_.diff-addition]:border-green-400 [&_.diff-addition]:pl-3 [&_.diff-addition]:py-2 [&_.diff-addition]:mb-2 [&_.diff-addition]:rounded-r
                      [&_.diff-deletion]:bg-red-50 [&_.diff-deletion]:border-l-4 [&_.diff-deletion]:border-red-400 [&_.diff-deletion]:pl-3 [&_.diff-deletion]:py-2 [&_.diff-deletion]:mb-2 [&_.diff-deletion]:rounded-r
                      [&_.text-red-600]:text-red-700 [&_.text-red-600]:font-medium
                      [&_.text-green-600]:text-green-700 [&_.text-green-600]:font-medium
                      [&_strong]:font-semibold [&_strong]:text-gray-800"
                    dangerouslySetInnerHTML={{ 
                      __html: formatDiffContent(diff.diff)
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Footer informativo */}
      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Los cambios se guardan automáticamente cuando editas el contenido
        </p>
      </div>
    </div>
  );
};
